/**
 * The review list for the `finding` output kind on the generic agent report
 * page: every finding this agent raised that waits for a person, with accept
 * (and what to do next) and dismiss, and the reviewed ones with Reopen for
 * whoever reviewed them. The same card and store functions the findings pages
 * use, so the triggers that enforce reviewers and stamp decision records fire
 * the same way. A finding is advice: accepting it changes nothing by itself.
 */

import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Callout } from '../../ds';
import { useAgentFindingsStore } from '../../../stores/prioritization/agentFindingsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { INTELLIGENCE_AGENTS } from '../../../lib/intelligence/agentMeta';
import type { AgentFinding } from '../../../lib/agentFindingsService';
import { FindingCard } from '../intelligence/FindingCard';
import { FindingAcceptedNextSteps } from '../intelligence/FindingAcceptedNextSteps';
import type { OutputReviewProps } from './types';
import { useOpenFindings } from './useOpenFindings';

const EMPTY: AgentFinding[] = [];

export function FindingsReview({ def, workspaceId, mayReview, onChanged }: OutputReviewProps) {
  const userId = useUserStore((s) => s.userId);
  const accept = useAgentFindingsStore((s) => s.accept);
  const dismiss = useAgentFindingsStore((s) => s.dismiss);
  const fetchActive = useAgentFindingsStore((s) => s.fetchActive);
  const fetchHistory = useAgentFindingsStore((s) => s.fetchHistory);
  const history = useAgentFindingsStore((s) => s.byAgent[def.agentType]?.history ?? EMPTY);
  const open = useOpenFindings(def.agentType);
  const labels = INTELLIGENCE_AGENTS[def.agentType]?.findingTypeLabels ?? {};
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [justAccepted, setJustAccepted] = useState<AgentFinding | null>(null);

  useEffect(() => { void fetchHistory(workspaceId, def.agentType); }, [workspaceId, def.agentType, fetchHistory]);

  const decide = async (id: string, act: 'accept' | 'dismiss') => {
    if (!userId) return;
    const finding = open.find((f) => f.id === id) ?? null;
    setBusyId(id);
    setError(null);
    const result = act === 'accept' ? await accept(def.agentType, id, userId) : await dismiss(def.agentType, id, userId);
    setBusyId(null);
    if (!result.success) { setError(result.error ?? 'Not saved'); return; }
    if (act === 'accept') setJustAccepted(finding);
    onChanged();
  };
  const reload = () => { void fetchActive(workspaceId, def.agentType); void fetchHistory(workspaceId, def.agentType); onChanged(); };

  return (
    <div className="space-y-2">
      {error && <Callout tone="coral">{error}</Callout>}
      {justAccepted && <FindingAcceptedNextSteps finding={justAccepted} onDismiss={() => setJustAccepted(null)} />}
      {!mayReview && open.length > 0 && (
        <Callout tone="neutral">Reviewing what {def.name} finds needs one of its review roles. A workspace administrator can grant one.</Callout>
      )}
      {open.length === 0 ? (
        <div className="card text-center py-10">
          <Sparkles className="h-6 w-6 mx-auto text-[var(--text-ter)]" />
          <p className="text-sm font-medium text-[var(--text-pri)] mt-2">Nothing waiting</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">{INTELLIGENCE_AGENTS[def.agentType]?.emptyBody ?? `Run ${def.name} to look for issues.`}</p>
        </div>
      ) : open.map((f) => (
        <FindingCard
          key={f.id}
          finding={f}
          typeLabel={labels[f.findingType] ?? f.findingType}
          onAccept={(id) => decide(id, 'accept')}
          onDismiss={(id) => decide(id, 'dismiss')}
          isProcessing={busyId === f.id}
          readOnly={!mayReview}
          userId={userId}
        />
      ))}
      {history.length > 0 && (
        <div className="pt-3 space-y-2">
          <p className="text-xs font-medium text-[var(--text-sec)]">Recently reviewed</p>
          {history.slice(0, 10).map((f) => (
            <FindingCard
              key={f.id}
              finding={f}
              typeLabel={labels[f.findingType] ?? f.findingType}
              onAccept={async () => {}}
              onDismiss={async () => {}}
              isProcessing={false}
              readOnly
              userId={userId}
              onReopened={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
}
